/* eslint-disable @typescript-eslint/no-unused-vars */
// Common service for both prime and company processes/tasks

import {
  ProcessTask,
  ProcessTaskWithArtifacts,
  ProcessValidation,
  ProcessTransition,
  ProcessArtifactRole,
  ArtifactRole,
  ValidationError,
  ValidationWarning,
  ValidationSuggestion,
} from '../types/processTask';
import { ProcessService } from './api/processService';
import { CompanyProcessService } from './companyProcessService';
import {
  collectProcessCentricTransitions,
  mapPrimeProcessToProcessTask,
  mapProcessCentricViewToProcessTaskWithArtifacts,
} from './processTaskPrimeMapper';

export class ProcessTaskService {
  private context: 'prime' | 'company';
  private companyProcessService: CompanyProcessService;

  constructor(context: 'prime' | 'company') {
    this.context = context;
    this.companyProcessService = new CompanyProcessService();
  }

  // Get process/task with all artifacts and transitions
  async getProcessWithArtifacts(processId: string): Promise<ProcessTaskWithArtifacts> {
    try {
      if (this.context === 'prime') {
        const view = await ProcessService.getProcessCentricView(processId);
        const transitions = collectProcessCentricTransitions(view);
        const base = mapProcessCentricViewToProcessTaskWithArtifacts(view, transitions);
        const artifacts = this.groupArtifactsByRole(transitions);
        const validation = await this.validateProcess(base, artifacts, transitions);
        return { ...base, artifacts, validation };
      } else {
        return await this.companyProcessService.getProcessWithArtifacts(processId);
      }
    } catch (error) {
      console.error('Error getting process with artifacts:', error);
      throw error;
    }
  }

  // Get all processes for an artifact
  async getProcessesForArtifact(artifactId: string): Promise<ProcessTask[]> {
    try {
      if (this.context === 'prime') {
        const processes = await ProcessService.getByArtifactId(artifactId);
        return processes.map(mapPrimeProcessToProcessTask);
      } else {
        return await this.companyProcessService.getProcessesForArtifact(artifactId);
      }
    } catch (error) {
      console.error('Error getting processes for artifact:', error);
      throw error;
    }
  }

  // Get all processes (for process-centric entry point)
  async getAllProcesses(): Promise<ProcessTask[]> {
    try {
      if (this.context === 'prime') {
        const processes = await ProcessService.getAll();
        return processes.map(mapPrimeProcessToProcessTask);
      } else {
        return await this.companyProcessService.getAllProcesses();
      }
    } catch (error) {
      console.error('Error getting all processes:', error);
      throw error;
    }
  }

  // Add artifact to process with role
  async addArtifactToProcess(processId: string, artifactId: string, role: string): Promise<void> {
    try {
      // This would create a transition for the artifact in the process
      // TODO: Implement based on the specific API structure
      console.log(`Adding artifact ${artifactId} to process ${processId} with role ${role}`);
    } catch (error) {
      console.error('Error adding artifact to process:', error);
      throw error;
    }
  }

  // Remove artifact from process
  async removeArtifactFromProcess(processId: string, artifactId: string): Promise<void> {
    try {
      // This would remove all transitions for the artifact in the process
      // TODO: Implement based on the specific API structure
      console.log(`Removing artifact ${artifactId} from process ${processId}`);
    } catch (error) {
      console.error('Error removing artifact from process:', error);
      throw error;
    }
  }

  // Update artifact role in process
  async updateArtifactRole(processId: string, artifactId: string, newRole: string): Promise<void> {
    try {
      // This would update the transition type for the artifact
      // TODO: Implement based on the specific API structure
      console.log(`Updating artifact ${artifactId} role to ${newRole} in process ${processId}`);
    } catch (error) {
      console.error('Error updating artifact role:', error);
      throw error;
    }
  }

  // Validate process completeness
  async validateProcess(
    process: ProcessTask,
    artifacts: ProcessTaskWithArtifacts['artifacts'],
    transitions: ProcessTransition[]
  ): Promise<ProcessValidation> {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];
    const suggestions: ValidationSuggestion[] = [];

    // Check if process has at least one process item
    if (artifacts.processItems.length === 0) {
      errors.push({
        id: 'missing-process-item',
        type: 'MISSING_ARTIFACT',
        message: 'Process must have at least one PROCESSITEM artifact',
        fixable: true,
        suggested_fix: 'Add an artifact with PROCESSITEM role',
      });
    }

    // Check if all artifacts have transitions
    const allArtifacts = [
      ...artifacts.processItems,
      ...artifacts.materials,
      ...artifacts.resources,
      ...artifacts.creations,
      ...artifacts.processItemsPersistent,
    ];

    for (const artifact of allArtifacts) {
      if (artifact.transitions.length === 0) {
        warnings.push({
          id: `no-transitions-${artifact.artifact_id}`,
          type: 'INCOMPLETE_PROCESS',
          message: `Artifact ${artifact.artifact_name} has no transitions`,
          artifact_id: artifact.artifact_id,
        });
      }
    }

    // Check for unused artifacts (artifacts with transitions but no role)
    const artifactIdsWithRoles = new Set(allArtifacts.map((a) => a.artifact_id));
    const artifactIdsWithTransitions = new Set(transitions.map((t) => t.artifact_id));

    for (const transition of transitions) {
      if (!artifactIdsWithRoles.has(transition.artifact_id)) {
        suggestions.push({
          id: `unused-artifact-${transition.artifact_id}`,
          type: 'ADD_ARTIFACT',
          message: `Artifact ${transition.artifact_name} has transitions but no defined role`,
          artifact_id: transition.artifact_id,
          action: 'Define role for this artifact',
        });
      }
    }

    return {
      is_complete: errors.length === 0,
      errors,
      warnings,
      suggestions,
    };
  }

  // Private helper methods
  private async getProcessTransitions(processId: string): Promise<ProcessTransition[]> {
    try {
      if (this.context === 'prime') {
        // TODO: Fix TransitionService.getByProcess method name
        // For now, return empty array
        return [];
      } else {
        // For company processes, we'll need to implement this
        // TODO: Implement company transition service
        return [];
      }
    } catch (error) {
      console.error('Error getting process transitions:', error);
      throw error;
    }
  }

  private groupArtifactsByRole(
    transitions: ProcessTransition[]
  ): ProcessTaskWithArtifacts['artifacts'] {
    const artifacts: ProcessTaskWithArtifacts['artifacts'] = {
      processItems: [],
      materials: [],
      resources: [],
      creations: [],
      processItemsPersistent: [],
    };

    // Group transitions by artifact and role
    const artifactMap = new Map<string, ProcessArtifactRole>();

    for (const transition of transitions) {
      if (!artifactMap.has(transition.artifact_id)) {
        artifactMap.set(transition.artifact_id, {
          artifact_id: transition.artifact_id,
          artifact_name: transition.artifact_name,
          role: transition.transition_type as ArtifactRole,
          is_required: true, // TODO: Determine from process definition
          transitions: [],
        });
      }

      const artifact = artifactMap.get(transition.artifact_id)!;
      artifact.transitions.push(transition);
    }

    // Group by role
    for (const artifact of Array.from(artifactMap.values())) {
      switch (artifact.role) {
        case 'PROCESSITEM':
          artifacts.processItems.push(artifact);
          break;
        case 'MATERIAL':
          artifacts.materials.push(artifact);
          break;
        case 'RESOURCE':
          artifacts.resources.push(artifact);
          break;
        case 'CREATION':
          artifacts.creations.push(artifact);
          break;
        case 'PROCESSITEM_PERSISTENT':
          artifacts.processItemsPersistent.push(artifact);
          break;
      }
    }

    return artifacts;
  }
}
